import type { Node as PmNode } from '@tiptap/pm/model'

export const PLACEHOLDER_CALLOUT_MARK_NAMES = [
  'notSpecified',
  'manualInstruction',
  'insufficientInformation',
] as const

export type PlaceholderCalloutMarkName =
  (typeof PLACEHOLDER_CALLOUT_MARK_NAMES)[number]

function isPlaceholderCalloutMarkName(
  name: string
): name is PlaceholderCalloutMarkName {
  return PLACEHOLDER_CALLOUT_MARK_NAMES.some((markName) => markName === name)
}

function textNodeHasPlaceholderCalloutMark(
  marks: ReadonlyArray<{ type: { name: string } }>
): boolean {
  return marks.some((mark) => isPlaceholderCalloutMarkName(mark.type.name))
}

/**
 * True when every text node in `block` is a placeholder callout (whitespace-only
 * unmarked text is ignored). Empty blocks are not covered.
 */
export function isNodeFullyCoveredByPlaceholderMarks(block: PmNode): boolean {
  if (block.content.size === 0) {
    return false
  }

  let hasCoveredText = false
  let hasUnmarkedContent = false

  block.descendants((node) => {
    if (!node.isText || node.text == null) {
      return
    }

    if (textNodeHasPlaceholderCalloutMark(node.marks)) {
      hasCoveredText = true
      return
    }

    if (node.text.trim() !== '') {
      hasUnmarkedContent = true
    }
  })

  return hasCoveredText && !hasUnmarkedContent
}

/** Insert a trailing empty paragraph when the last block is only a placeholder callout. */
export function shouldInsertTrailingParagraphAfter(
  lastNode: PmNode | null | undefined
): boolean {
  if (lastNode == null) {
    return false
  }

  if (lastNode.type.name === 'paragraph' && lastNode.content.size === 0) {
    return false
  }

  return isNodeFullyCoveredByPlaceholderMarks(lastNode)
}
